import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getArchivedEvents } from '../lib/api/events';
import type { Event } from '../types/database';
import { captureError } from '../lib/capture-error';
import { localDateToMs, parseLocalDate } from '../lib/dates';
import './PastEventsPage.css';

const PastEventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const archivedEvents = await getArchivedEvents();
        // Sort by start_date descending (most recent first)
        const sortedEvents = archivedEvents.sort(
          (a: Event, b: Event) => localDateToMs(b.start_date) - localDateToMs(a.start_date)
        );
        setEvents(sortedEvents);
      } catch (err) {
        captureError(err, 'Failed to load past events');
        setError('Failed to load past events');
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, []);

  if (loading) {
    return (
      <div className="container my-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container my-5">
        <div className="alert alert-danger text-center">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <h1 className="text-center">Past Events</h1>
      <p className="text-center">Take a look back at our previous outreach events and the impact we've made together!</p>
      
      {events.length === 0 ? (
        <div className="text-center">
          <p className="text-muted">No past events found.</p>
        </div>
      ) : (
        <div className="row">
          {events.map((event, index) => {
            return (
              <div className="col-md-4 mb-4" key={index}>
                <div className="card h-100 past-event-card">
                  {event.featured_image_url && (
                    <img 
                      src={event.featured_image_url} 
                      alt={event.title} 
                      className="card-img-top past-event-image" 
                    />
                  )}
                  <div className="card-body">
                    <h5 className="card-title">{event.title}</h5>
                    <p className="card-text">{event.description}</p>
                    <p className="card-text text-muted">
                      {event.start_date &&
                        parseLocalDate(event.start_date).toLocaleDateString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        })}
                    </p>
                    <div className="mt-3">
                      <Link to={`/events/${event.slug}`} className="btn btn-outline-primary btn-sm">
                        <i className="fas fa-eye me-1" aria-hidden></i>
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PastEventsPage;
